import { useState, type FormEvent } from 'react';
import { Navigate, useSearchParams, Link } from 'react-router';
import { FlaskConical, Mail } from 'lucide-react';
import { setDemo, useApp } from '../data/AppProvider';
import { errorMessage } from '../data/hooks';
import { Button, Field, Input, Notice } from '../components/ui';

export function SignInScreen() {
  const { data, session, backendConfigured } = useApp();
  const [params] = useSearchParams();
  const next = params.get('next') || '/';
  const [closed] = useState(() => {
    let flag = false;
    try { flag = window.sessionStorage.getItem('elio-account-closed') === '1'; window.sessionStorage.removeItem('elio-account-closed'); } catch { /* ignore */ }
    return flag || params.get('closed') === '1';
  });
  const [email, setEmail] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);

  if (session) return <Navigate to={next.startsWith('/') ? next : '/'} replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setState('sending');
    try {
      await data.signInWithEmail(email.trim(), `${window.location.origin}${next.startsWith('/') ? next : '/'}`);
      setState('sent');
    } catch (err) {
      setError(errorMessage(err));
      setState('idle');
    }
  };

  const verify = async (e: FormEvent) => {
    e.preventDefault();
    setCodeError(null);
    setVerifying(true);
    try {
      await data.verifyEmailCode(email.trim(), code.replace(/\s/g, ''));
      // The session listener redirects once signed in.
    } catch (err) {
      setCodeError(errorMessage(err));
    } finally {
      setVerifying(false);
    }
  };

  return (
    <main className="safe-top flex min-h-dvh flex-col items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center gap-3">
          <img src="/icon.svg" alt="" className="size-11" />
          <div>
            <h1 className="font-display text-3xl text-forest-800">Elio Travels</h1>
            <p className="text-sm text-muted">Your trip, understood. Your next step, clear.</p>
          </div>
        </div>

        <div className="card p-6">
          {!backendConfigured ? (
            <Notice tone="warn" title="Sign-in isn't configured in this build">
              This build has no Supabase connection, so accounts are unavailable. You can still explore the labelled demo.
            </Notice>
          ) : state === 'sent' ? (
            <div role="status">
              <div className="mb-3 flex size-12 items-center justify-center rounded-2xl bg-forest-50 text-forest-700"><Mail className="size-6" aria-hidden /></div>
              <h2 className="font-display text-xl">Check your email</h2>
              <p className="mt-1 text-sm text-muted">We sent a sign-in link to <strong className="text-ink break-anywhere">{email}</strong>. Open it on any device, or type the code from the email here.</p>
              <form onSubmit={verify} className="mt-5 space-y-3" noValidate>
                <Field label="Code from the email" error={codeError}>
                  {(id, d) => <Input id={id} aria-describedby={d} inputMode="numeric" autoComplete="one-time-code" maxLength={10} className="tracking-[0.3em] tabular-nums" value={code} onChange={(e) => setCode(e.target.value.replace(/[^\d\s]/g, ''))} />}
                </Field>
                <Button type="submit" className="w-full" busy={verifying} disabled={code.replace(/\s/g, '').length < 6}>Sign in with code</Button>
              </form>
              <Button variant="ghost" className="mt-3 -ml-2" onClick={() => { setState('idle'); setCode(''); setCodeError(null); }}>Use a different email</Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" noValidate>
          {closed && <Notice tone="success" className="mb-4" title="Your account has been closed">Your personal data has been deleted. Thank you for travelling with Elio.</Notice>}
              <h2 className="font-display text-xl">Sign in or create an account</h2>
              <Field label="Email" error={error} hint="We'll email you a one-time sign-in link. No password needed.">
                {(id, d) => <Input id={id} aria-describedby={d} type="email" autoComplete="email" inputMode="email" required value={email} onChange={(e) => setEmail(e.target.value)} />}
              </Field>
              <Button type="submit" size="lg" className="w-full" busy={state === 'sending'} disabled={!/^\S+@\S+\.\S+$/.test(email)}>Email me a sign-in link</Button>
              <p className="mt-3 text-xs leading-relaxed text-muted">By continuing, you agree to the <Link to="/terms" className="underline">Terms</Link> and <Link to="/privacy" className="underline">Privacy Policy</Link>, including how Elio uses AI (OpenAI) to answer your messages.</p>
            </form>
          )}
        </div>

        <div className="mt-6 rounded-2xl border border-dashed border-gold/50 bg-gold-soft/50 p-4 text-sm">
          <p className="flex items-center gap-2 font-medium"><FlaskConical className="size-4" aria-hidden /> Just looking?</p>
          <p className="mt-1 text-muted">Open a demo with fictional travellers and bookings. It's clearly labelled, runs only in your browser, and nothing is saved.</p>
          <Button variant="gold" size="sm" className="mt-3" onClick={() => setDemo(true)}>Open the demo</Button>
        </div>
      </div>
    </main>
  );
}
